import React from 'react';
import { FileText, ListChecks, ShieldCheck } from 'lucide-react';

const cards = [
  {
    icon: FileText,
    title: 'Asset Documentation First',
    copy: 'Start with documented assets, issuer records, campaign terms, warrants, receipts, trust documents, collateral files, or registry records before configuring the token.',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance-Ready Token Workflow',
    copy: 'Set transfer rules, holder eligibility, wallet settlement, approval states, and human review before activation.',
  },
  {
    icon: ListChecks,
    title: 'Records and Controls',
    copy: 'Maintain holder records, evidence files, transfer logs, and reporting exports for review, accounting, and audit support.',
  },
];

const comparison = [
  ['Starts with deployment', 'Starts with asset documentation'],
  ['Focuses on token configuration', 'Focuses on compliant tokenization workflow'],
  ['User defines asset claims', 'Requires evidence and responsible-party review'],
  ['May enable fast launch', 'Requires complete files and human review'],
  ['Token is the product', 'Documented asset workflow is the product'],
];

const BeforeDeployment = () => (
  <section id="before-deployment" className="py-24 lg:py-32 border-t border-border bg-muted/20">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-3xl mb-12">
        <div className="section-label mb-4">Before Tokenization</div>
        <h2 className="font-display text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-foreground">
          Before tokenizing an asset, prepare the file.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          Creating a token is simple. Building a compliant asset tokenization program requires documents, records, controls, and responsible parties.
        </p>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Tangibl3Net helps issuers review documented assets, responsible parties, evidence files, holder rights, wallet setup, transfer policy, holder records, and compliance guardrails.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-10">
        {cards.map((card) => (
          <article key={card.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <card.icon className="w-5 h-5 text-primary mb-4" />
            <h3 className="font-display text-xl font-bold text-foreground">{card.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{card.copy}</p>
          </article>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
        <div className="section-label mb-3">Not a Token Launcher</div>
        <h3 className="font-display text-2xl lg:text-3xl font-bold text-foreground">
          A compliant asset tokenization workflow.
        </h3>
        <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl">
          Tangibl3Net does not start with minting. It starts with documented assets, responsible parties, evidence files, transfer policies, holder records, and compliance guardrails.
        </p>
        <div className="mt-6 overflow-hidden rounded-xl border border-border">
          <div className="grid grid-cols-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <div className="p-3 border-r border-border">Token launcher</div>
            <div className="p-3">Tangibl3Net</div>
          </div>
          {comparison.map(([launcher, tangible]) => (
            <div key={launcher} className="grid grid-cols-2 border-t border-border text-sm">
              <div className="p-3 border-r border-border text-muted-foreground">{launcher}</div>
              <div className="p-3 text-foreground">{tangible}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default BeforeDeployment;
